import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users,
  UserCheck,
  LogIn,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowUpRight,
  ArrowDownRight,
  Trophy,
  Radio,
  MessageSquare,
  Users2,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PERCENTILE_STYLES = {
  excelente: { label: 'Top 25%', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  bom: { label: 'Top 50%', cls: 'bg-sky-100 text-sky-700 border-sky-200' },
  regular: { label: 'Top 75%', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  baixo: { label: 'Acompanhar', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
};

const STATUS_STYLES = {
  ativo: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  pausado: 'bg-amber-100 text-amber-700 border-amber-200',
  bloqueado: 'bg-rose-100 text-rose-700 border-rose-200',
};

const DESTINO_ICON = {
  canal: Radio,
  grupo: Users2,
  comunidade: MessageSquare,
};

const formatNumber = (n) => new Intl.NumberFormat('pt-BR').format(n || 0);
const formatPct = (n) => `${(n || 0).toFixed(n % 1 === 0 ? 0 : 1)}%`;

const VariationPill = ({ pct }) => {
  if (pct === 0 || pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
        <Minus className="h-3 w-3" />
        Estável
      </span>
    );
  }
  const positive = pct > 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  const cls = positive ? 'text-emerald-600' : 'text-rose-600';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cls}`}>
      <Icon className="h-3 w-3" />
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

const KpiCard = ({ title, value, subtitle, delta24h, pctChange, icon: Icon, suffix = '', testId }) => (
  <Card className="rounded-2xl border-slate-200/80 shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <VariationPill pct={pctChange} />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <p className="text-3xl font-bold tracking-tight text-slate-900">
          {typeof value === 'number' ? formatNumber(value) : value}
          {suffix && <span className="text-xl font-semibold text-slate-500 ml-0.5">{suffix}</span>}
        </p>
        <p className="text-xs text-slate-500">
          {subtitle}{' '}
          <span className="font-semibold text-slate-700">
            {typeof delta24h === 'number' ? (delta24h >= 0 ? `+${formatNumber(delta24h)}` : formatNumber(delta24h)) : delta24h}
          </span>{' '}
          nas últimas 24h
        </p>
      </div>
    </CardContent>
  </Card>
);

export const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/stats/dashboard/v2`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        console.error('Dashboard error', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  const k = data?.kpis || {};
  const topLeaders = data?.top_leaders || [];
  const allLeaders = data?.all_leaders || [];
  const evolution = data?.evolution || [];
  const fontes = data?.fontes || [];
  const maxTop = data?.max_top_ingressantes || 1;

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
            <p className="text-slate-500 mt-1 text-sm">
              Visão geral do desempenho da sua rede de apoiadores em tempo real.
            </p>
          </div>
          <Badge variant="outline" className="rounded-full px-3 py-1.5 self-start border-slate-200 bg-white text-slate-600 font-medium">
            <span className="h-2 w-2 rounded-full bg-emerald-500 mr-2 animate-pulse" />
            Atualizado agora
          </Badge>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            testId="kpi-liderancas"
            title="Lideranças ativas"
            icon={Users}
            value={k.liderancas_ativas?.value}
            subtitle="Novas:"
            delta24h={k.liderancas_ativas?.delta_24h}
            pctChange={k.liderancas_ativas?.pct_change}
          />
          <KpiCard
            testId="kpi-apoiadores"
            title="Apoiadores cadastrados"
            icon={UserCheck}
            value={k.apoiadores_cadastrados?.value}
            subtitle="Novos:"
            delta24h={k.apoiadores_cadastrados?.delta_24h}
            pctChange={k.apoiadores_cadastrados?.pct_change}
          />
          <KpiCard
            testId="kpi-ingressantes"
            title="Ingressantes (entradas)"
            icon={LogIn}
            value={k.ingressantes?.value}
            subtitle="Novos:"
            delta24h={k.ingressantes?.delta_24h}
            pctChange={k.ingressantes?.pct_change}
          />
          <KpiCard
            testId="kpi-conversao"
            title="Taxa de conversão"
            icon={TrendingUp}
            value={formatPct(k.taxa_conversao?.value)}
            subtitle="Em 24h:"
            delta24h={`${formatPct(k.taxa_conversao?.delta_24h)}`}
            pctChange={k.taxa_conversao?.pct_change}
          />
        </div>

        {/* Top lideranças + Fontes de ingressos */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Top lideranças */}
          <Card className="rounded-2xl border-slate-200/80 shadow-sm lg:col-span-3">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-500" />
                    Top 5 lideranças
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Ranking por ingressantes gerados</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {topLeaders.length === 0 && (
                <p className="text-sm text-slate-500 py-8 text-center">
                  Nenhuma liderança com atividade ainda.
                </p>
              )}
              {topLeaders.map((l, idx) => {
                const pct = maxTop > 0 ? (l.ingressantes / maxTop) * 100 : 0;
                return (
                  <div key={l.id} data-testid={`top-leader-${idx}`} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold shrink-0">
                          {idx + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {l.apelido || l.nome}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {formatNumber(l.acessos)} acessos · conv. {formatPct(l.taxa_conversao)}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-slate-900 shrink-0 ml-3">
                        {formatNumber(l.ingressantes)}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          background: 'linear-gradient(90deg, hsl(225 64% 14%), hsl(142 71% 45%))',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Fontes de ingressos */}
          <Card className="rounded-2xl border-slate-200/80 shadow-sm lg:col-span-2">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold">Fontes de ingressos</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Destinos com mais entradas</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {fontes.length === 0 && (
                <p className="text-sm text-slate-500 py-8 text-center">Nenhum destino configurado.</p>
              )}
              {fontes.slice(0, 6).map((f) => {
                const Icon = DESTINO_ICON[f.tipo] || Radio;
                return (
                  <div
                    key={f.id}
                    data-testid={`fonte-${f.id}`}
                    className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{f.nome}</p>
                        <p className="text-xs text-slate-500 capitalize">{f.tipo}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-slate-900 ml-2">
                      {formatNumber(f.ingressantes)}
                    </span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Evolução de ingressos */}
        <Card className="rounded-2xl border-slate-200/80 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Evolução de ingressos (14 dias)</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Entradas em canais, grupos e comunidades</p>
          </CardHeader>
          <CardContent>
            {evolution.length === 0 ? (
              <p className="text-sm text-slate-500 py-12 text-center">
                Sem dados suficientes ainda — assim que apoiadores começarem a entrar, este gráfico será preenchido.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={evolution} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: '1px solid #E2E8F0',
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Line type="monotone" dataKey="canal" name="Canais" stroke="hsl(225 64% 14%)" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="grupo" name="Grupos" stroke="hsl(142 71% 45%)" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="comunidade" name="Comunidades" stroke="hsl(221 83% 53%)" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Desempenho por liderança */}
        <Card className="rounded-2xl border-slate-200/80 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Desempenho por liderança</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Comparativo com percentil de performance</p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leader-perf-table">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold">Liderança</th>
                    <th className="text-right px-4 py-3 font-semibold">Acessos</th>
                    <th className="text-right px-4 py-3 font-semibold">Ingressantes</th>
                    <th className="text-right px-4 py-3 font-semibold">Apoiadores</th>
                    <th className="text-right px-4 py-3 font-semibold">Conversão</th>
                    <th className="text-center px-4 py-3 font-semibold">Desempenho</th>
                    <th className="text-center px-6 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allLeaders.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-6 py-10 text-center text-slate-500">
                        Cadastre lideranças para visualizar o desempenho.
                      </td>
                    </tr>
                  )}
                  {allLeaders.map((l) => {
                    const perc = PERCENTILE_STYLES[l.percentile] || PERCENTILE_STYLES.baixo;
                    const statusCls = STATUS_STYLES[l.status] || 'bg-slate-100 text-slate-700 border-slate-200';
                    return (
                      <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-3.5">
                          <div className="font-semibold text-slate-900">{l.apelido || l.nome}</div>
                          {l.apelido && (
                            <div className="text-xs text-slate-500 truncate max-w-[240px]">{l.nome}</div>
                          )}
                        </td>
                        <td className="text-right px-4 py-3.5 font-medium text-slate-700">{formatNumber(l.acessos)}</td>
                        <td className="text-right px-4 py-3.5 font-bold text-slate-900">{formatNumber(l.ingressantes)}</td>
                        <td className="text-right px-4 py-3.5 text-slate-700">{formatNumber(l.apoiadores)}</td>
                        <td className="text-right px-4 py-3.5 text-slate-700">{formatPct(l.taxa_conversao)}</td>
                        <td className="text-center px-4 py-3.5">
                          <Badge variant="outline" className={`rounded-full font-medium ${perc.cls}`}>
                            {perc.label}
                          </Badge>
                        </td>
                        <td className="text-center px-6 py-3.5">
                          <Badge variant="outline" className={`rounded-full font-medium capitalize ${statusCls}`}>
                            {l.status || '—'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
